import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Body,
  Button,
  Card,
  Grid,
  Heading,
  I,
  Label,
  Stack,
} from '@leanin/ui';
import { FlowShell } from '@/components/onboarding/FlowShell';
import { Picture } from '@/components/media/Picture';
import nlNetworkPage from '@/assets/onboarding/nl-network-page.png?w=440;880;1320&format=avif;webp;png&as=picture';
import { NL_ROLE, NL_STEPS } from './steps';

// Network Leader onboarding tour — step 3 of 4.
// Tour of the Network page surface — what each top-level tab is for,
// followed by an annotated screenshot of an example Network.

type Tab = {
  readonly name: string;
  readonly sub: string;
};

const TABS: ReadonlyArray<Tab> = [
  {
    name: 'Overview',
    sub: 'This is the first thing community members see when they visit your Network page. Use this space to share key information about your Network and help members feel welcomed and informed.',
  },
  {
    name: 'Feed',
    sub: 'Share community updates, announcements, and conversations with your members.',
  },
  {
    name: 'Members',
    sub: 'Add members and view your Network member directory.',
  },
  {
    name: 'Circles',
    sub: 'Circles connected to your Network will appear here. Members who are not yet part of a Circle can browse available Circles and join those that are open.',
  },
  {
    name: 'Events',
    sub: 'Share your Network’s programming, gatherings, and upcoming events.',
  },
  {
    name: 'Resources',
    sub: 'Upload and organize content for your Network, including guides, templates, articles, and more.',
  },
];

export const Route = createFileRoute('/onboarding/nl/network-page')({
  component: NLOnboardingNetworkPagePage,
});

function NLOnboardingNetworkPagePage() {
  const navigate = useNavigate();
  return (
    <FlowShell
      role={NL_ROLE}
      stepIndex={2}
      steps={NL_STEPS}
      minutes={2}
      title="Your Network page is the home for your local community."
      primary={
        <Button
          variant="primary"
          iconRight={I.arrow}
          onClick={() => navigate({ to: '/onboarding/nl/access-network' })}
        >
          Next
        </Button>
      }
      secondary={
        <Button
          variant="ghost"
          onClick={() => navigate({ to: '/onboarding/nl/hub' })}
        >
          Back
        </Button>
      }
    >
      <Stack gap="lg">
        <Stack gap="md">
          <Heading level={2}>Your Network Page</Heading>
          <Body size="md">Each Network includes the following tabs to manage:</Body>
        </Stack>

        <Grid columns="repeat(auto-fit, minmax(280px, 1fr))" gap="lg">
          <TabBulletList tabs={TABS.slice(0, 3)} />
          <TabBulletList tabs={TABS.slice(3)} />
        </Grid>

        <Card pad="flush" clip>
          <Picture
            image={nlNetworkPage}
            alt="Example Lean In Network page with the tab bar highlighted"
            sizes="(max-width: 720px) 100vw, 880px"
            style={{ width: '100%', height: 'auto', display: 'block' }}
          />
        </Card>
      </Stack>
    </FlowShell>
  );
}

function TabBulletList({ tabs }: { tabs: ReadonlyArray<Tab> }) {
  return (
    <ul
      style={{
        margin: 0,
        // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- semantic <ul> with bullet padding; no Box prop
        paddingInlineStart: 'var(--space-lg)',
        display: 'flex',
        flexDirection: 'column',
        // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- semantic <ul> with flex-column children; no Box prop
        gap: 'var(--space-md)',
      }}
    >
      {tabs.map((tab) => (
        <li key={tab.name}>
          <Stack gap="xs">
            <Label size="lg">{tab.name}</Label>
            <Body size="md">{tab.sub}</Body>
          </Stack>
        </li>
      ))}
    </ul>
  );
}
